/**
 * useSessionToken
 *
 * /auth/session-token からJWTを取得するフック。
 * - mode: 'test' | 'production' でテスト/本番Sessionを分離
 * - 本番開始ボタンを押すたびに新しいsessionIdのJWTが発行される
 * - Turnstile対応構造（Phase 1はturnstileToken不要）
 */

import { useState, useCallback } from 'react';

const WORKER_URL = import.meta.env.VITE_WORKER_URL as string ?? 'http://localhost:8787';

export interface SessionTokenResult {
  sessionToken: string;
  sessionId: string;
  mode: 'test' | 'production';
  expiresAt?: number;
  testMinutes?: number;
  hardExpiresAt?: number;
  remainingSeconds?: number;
}

interface FetchTokenOptions {
  mode?: 'test' | 'production';
  eventId?: string | null;
  eventName?: string | null;
  contractedMinutes?: number;  // 本番契約時間（test時は60固定）
  turnstileToken?: string;     // Phase 1は未使用
  adminSecret?: string;        // 管理者シークレット
}

// 接続テストのトークンをイベントごとにブラウザへ保存し、再読み込みしても同じテストの続きに戻る
const TEST_KEY = (eventId: string) => `amo_test_token:${eventId}`;
function tokenExp(token: string): number {
  try {
    const part = token.split('.')[1].replace(/-/g, '+').replace(/_/g, '/');
    return (JSON.parse(atob(part)) as { exp?: number }).exp ?? 0;
  } catch { return 0; }
}
export function clearSavedTestToken(eventId: string | null | undefined): void {
  if (!eventId) return;
  try { localStorage.removeItem(TEST_KEY(eventId)); } catch { /* ignore */ }
}

export function useSessionToken() {
  const [result, setResult] = useState<SessionTokenResult | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  const fetchToken = useCallback(
    async (opts: FetchTokenOptions = {}): Promise<SessionTokenResult | null> => {
      setLoading(true);
      setError(null);
      const isTest = (opts.mode ?? 'test') === 'test' && !opts.adminSecret && Boolean(opts.eventId);
      if (isTest) {
        // 保存済みのテストがまだ有効なら、それを使う（回数を使わない）
        try {
          const saved = JSON.parse(localStorage.getItem(TEST_KEY(opts.eventId!)) ?? 'null') as SessionTokenResult | null;
          if (saved?.sessionToken && tokenExp(saved.sessionToken) > Math.floor(Date.now() / 1000) + 30) {
            setResult(saved);
            setLoading(false);
            return saved;
          }
        } catch { /* ignore */ }
      }
      try {
        const body: Record<string, unknown> = {
          mode: opts.mode ?? 'test',
          contractedMinutes: opts.mode === 'production'
            ? (opts.contractedMinutes ?? 120)
            : 10,  // テストは常に10分
        };
        if (opts.eventId) body.eventId = opts.eventId;
        if (opts.eventName) body.eventName = opts.eventName;
        if (opts.turnstileToken) body.turnstileToken = opts.turnstileToken;
        if (opts.adminSecret) body.adminSecret = opts.adminSecret;

        const res = await fetch(`${WORKER_URL}/auth/session-token`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(body),
        });

        if (!res.ok) {
          const errData = (await res.json().catch(() => ({}))) as { error?: string; message?: string };
          if (errData.error === 'invalid_admin_secret') { try { sessionStorage.removeItem('amo_admin_secret'); } catch { /* ignore */ } }
          throw new Error(errData.message ?? errData.error ?? `HTTP ${res.status}`);
        }

        const data = (await res.json()) as SessionTokenResult;
        if (isTest && data.mode === 'test') {
          try { localStorage.setItem(TEST_KEY(opts.eventId!), JSON.stringify(data)); } catch { /* ignore */ }
        }
        setResult(data);
        return data;
      } catch (e) {
        const msg = e instanceof Error ? e.message : 'Token fetch failed';
        setError(msg);
        return null;
      } finally {
        setLoading(false);
      }
    },
    []
  );

  /** Stripe Checkout 検証 → JWT取得 */
  const verifyCheckout = useCallback(
    async (checkoutSessionId: string): Promise<SessionTokenResult | null> => {
      setLoading(true);
      setError(null);
      try {
        const res = await fetch(`${WORKER_URL}/checkout/verify`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ checkoutSessionId }),
        });
        if (!res.ok) {
          const errData = (await res.json().catch(() => ({}))) as { error?: string; message?: string };
          if (errData.error === 'invalid_admin_secret') { try { sessionStorage.removeItem('amo_admin_secret'); } catch { /* ignore */ } }
          throw new Error(errData.message ?? errData.error ?? `HTTP ${res.status}`);
        }
        const data = (await res.json()) as SessionTokenResult;
        setResult(data);
        return data;
      } catch (e) {
        const msg = e instanceof Error ? e.message : 'Checkout verify failed';
        setError(msg);
        return null;
      } finally {
        setLoading(false);
      }
    },
    []
  );

  /**
   * 本番翻訳開始 — /event/start-translation を Authorization: Bearer で呼ぶ
   * managementToken は URL・body に含めず、HTTPSヘッダーのみで送信
   */
  const startTranslation = useCallback(
    async (
      eventId: string,
      managementToken: string,
      eventName?: string | null
    ): Promise<SessionTokenResult | null> => {
      setLoading(true);
      setError(null);
      try {
        const body: Record<string, unknown> = { eventId };
        if (eventName) body.eventName = eventName;

        const res = await fetch(`${WORKER_URL}/event/start-translation`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${managementToken}`,
          },
          body: JSON.stringify(body),
        });

        if (!res.ok) {
          const errData = (await res.json().catch(() => ({}))) as { error?: string; message?: string };
          if (errData.error === 'invalid_admin_secret') { try { sessionStorage.removeItem('amo_admin_secret'); } catch { /* ignore */ } }
          throw new Error(errData.message ?? errData.error ?? `HTTP ${res.status}`);
        }

        const data = (await res.json()) as SessionTokenResult;
        setResult(data);
        return data;
      } catch (e) {
        const msg = e instanceof Error ? e.message : 'Start translation failed';
        setError(msg);
        return null;
      } finally {
        setLoading(false);
      }
    },
    []
  );

  /** 現在のtokenを破棄して新しいSessionを開始する */
  const resetToken = useCallback(() => {
    setResult(null);
    setError(null);
  }, []);

  return {
    sessionToken: result?.sessionToken ?? null,
    sessionId: result?.sessionId ?? null,
    mode: result?.mode ?? null,
    expiresAt: result?.expiresAt ?? null,
    testMinutes: result?.testMinutes ?? null,
    hardExpiresAt: result?.hardExpiresAt ?? null,
    fetchToken,
    verifyCheckout,
    startTranslation,
    resetToken,
    error,
    loading,
  };
}
